'use client';

import Link from 'next/link';
import { useMemo } from 'react';
import type { ReactElement } from 'react';
import PageEmptyState from '@/components/PageEmptyState';
import { withLocalePrefix } from '@/lib/localePath';
import type { Locale } from '@/lib/localePath';
import type { TraitMeta } from '@/lib/meta';
import {
  formatTraitThresholdSlugToken,
  getTraitPathFromMeta,
  getTraitThresholdSlugValues,
  isLeaderTraitId,
} from '@/lib/traitPath';
import { ScrollToTopButton } from './ScrollToTopButton';
import '@/styles/InfoPage.css';

type Props = {
  locale: Locale;
  traits: TraitMeta[];
  league: 'npb' | 'mlb';
  scope: 'career' | 'season' | 'leader';
  careerListMode?: 'ranking' | 'projection';
};

type Group = {
  key: string;
  labelJa: string;
  labelEn: string;
  labelEs: string;
  traits: TraitMeta[];
};
type PlayerType = 'batter' | 'pitcher';

const GROUPS: { key: string; labelJa: string; labelEn: string; labelEs: string; playerType: 'batter' | 'pitcher'; recordScope: 'career' | 'season' | 'leader' }[] = [
  { key: 'batter_career',  labelJa: '野手',  labelEn: 'Batters',   labelEs: 'Bateadores',   playerType: 'batter',  recordScope: 'career'  },
  { key: 'pitcher_career', labelJa: '投手',  labelEn: 'Pitchers',  labelEs: 'Lanzadores',   playerType: 'pitcher', recordScope: 'career'  },
  { key: 'batter_season',  labelJa: '野手',  labelEn: 'Batters',   labelEs: 'Bateadores',   playerType: 'batter',  recordScope: 'season'  },
  { key: 'pitcher_season', labelJa: '投手',  labelEn: 'Pitchers',  labelEs: 'Lanzadores',   playerType: 'pitcher', recordScope: 'season'  },
  { key: 'batter_leader',  labelJa: '野手',  labelEn: 'Batters',   labelEs: 'Bateadores',   playerType: 'batter',  recordScope: 'leader'  },
  { key: 'pitcher_leader', labelJa: '投手',  labelEn: 'Pitchers',  labelEs: 'Lanzadores',   playerType: 'pitcher', recordScope: 'leader'  },
];

const SCOPE_LABEL = {
  career: { ja: '通算記録', en: 'Career Records', es: 'Registros de Carrera' },
  season: { ja: 'シーズン記録', en: 'Season Records', es: 'Registros de Temporada' },
};

const COMBINED_LABEL = {
  ja: '日米合算',
  en: 'Major League Baseball (MLB) + Nippon Professional Baseball (NPB) Combined',
  es: 'Grandes Ligas (MLB) + Béisbol Profesional Japonés (NPB) Combinado',
};

const CAREER_MODE_TABS = {
  ranking: { ja: '通算記録', en: 'Career Stats', es: 'Estadísticas de Carrera' },
  projection: {
    ja: '通算記録達成まで',
    en: 'Career Milestone This Season',
    es: 'Meta de Carrera en Esta Temporada',
  },
} as const;

const CAREER_PROJECTION_EXCLUDED_SUFFIXES = new Set([
  'batting_average',
  'on_base_percentage',
  'slugging_percentage',
  'ops',
  'win_rate',
  'era',
  'whip',
]);

// Align list ordering with Player page stat column order.
const BATTER_METRIC_ORDER = [
  'game',
  'plate_appearances',
  'at_bats',
  'runs',
  'hits',
  'doubles',
  'triples',
  'home_runs',
  'total_bases',
  'rbi',
  'steals',
  'caught_stealing',
  'sac_bunts',
  'sac_flies',
  'walks',
  'intentional_walks',
  'hit_by_pitch',
  'strikeouts',
  'grounded_into_double_play',
  'batting_average',
  'on_base_percentage',
  'slugging_percentage',
  'ops',
] as const;

const PITCHER_METRIC_ORDER = [
  'appearances',
  'starts',
  'complete_games',
  'shutouts',
  'no_walks',
  'wins',
  'losses',
  'saves',
  'holds',
  'win_rate',
  'batters_faced',
  'innings_pitched',
  'hits_allowed',
  'home_runs_allowed',
  'walks_allowed',
  'intentional_walks_allowed',
  'hit_by_pitch_allowed',
  'strikeouts',
  'wild_pitches',
  'balks',
  'runs_allowed',
  'earned_runs_allowed',
  'era',
  'whip',
] as const;

const BATTER_ORDER_MAP = new Map<string, number>(BATTER_METRIC_ORDER.map((k, i) => [k, i]));
const PITCHER_ORDER_MAP = new Map<string, number>(PITCHER_METRIC_ORDER.map((k, i) => [k, i]));

const BATTER_SPECIAL_SEASON_ORDER = new Map<string, number>([
  ['club_30_30', 100],
  ['club_40_40', 101],
  ['club_50_50', 102],
  ['club_20_20_20', 103],
  ['club_20_20_20_20', 104],
  ['triple_three', 105],
]);

const SEASON_LINK_LABELS: Record<Locale, Record<string, string>> = {
  ja: {
    game: '試合', plate_appearances: '打席', at_bats: '打数', runs: '得点', hits: '安打', doubles: '二塁打', triples: '三塁打', home_runs: '本塁打', total_bases: '塁打', rbi: '打点', steals: '盗塁', caught_stealing: '盗塁刺', sac_bunts: '犠打', sac_flies: '犠飛', walks: '四球', intentional_walks: '敬遠', hit_by_pitch: '死球', strikeouts: '三振', grounded_into_double_play: '併殺打', batting_average: '打率', on_base_percentage: '出塁率', slugging_percentage: '長打率', ops: 'OPS', appearances: '登板', starts: '先発', complete_games: '完投', shutouts: '完封', no_walks: '無四球試合', wins: '勝利', losses: '敗戦', saves: 'セーブ', holds: 'ホールド', win_rate: '勝率', batters_faced: '対戦打者', innings_pitched: '投球回', hits_allowed: '被安打', home_runs_allowed: '被本塁打', walks_allowed: '与四球', intentional_walks_allowed: '与敬遠', hit_by_pitch_allowed: '与死球', runs_allowed: '失点', earned_runs_allowed: '自責点', era: '防御率', whip: 'WHIP', wild_pitches: '暴投', balks: 'ボーク', club_30_30: '30-30クラブ', club_40_40: '40-40クラブ', club_50_50: '50-50クラブ', club_20_20_20: '20-20-20クラブ', club_20_20_20_20: '20-20-20-20クラブ', triple_three: 'トリプルスリー',
  },
  en: {
    game: 'Games', plate_appearances: 'Plate Appearances', at_bats: 'At Bats', runs: 'Runs', hits: 'Hits', doubles: 'Doubles', triples: 'Triples', home_runs: 'Home Runs', total_bases: 'Total Bases', rbi: 'RBIs', steals: 'Stolen Bases', caught_stealing: 'Caught Stealing', sac_bunts: 'Sacrifice Bunts', sac_flies: 'Sacrifice Flies', walks: 'Walks', intentional_walks: 'Intentional Walks', hit_by_pitch: 'Hit by Pitch', strikeouts: 'Strikeouts', grounded_into_double_play: 'Grounded into Double Play', batting_average: 'Batting Average', on_base_percentage: 'On-base Percentage', slugging_percentage: 'Slugging Percentage', ops: 'OPS', appearances: 'Appearances', starts: 'Starts', complete_games: 'Complete Games', shutouts: 'Shutouts', no_walks: 'Walk-free Games', wins: 'Wins', losses: 'Losses', saves: 'Saves', holds: 'Holds', win_rate: 'Win Percentage', batters_faced: 'Batters Faced', innings_pitched: 'Innings Pitched', hits_allowed: 'Hits Allowed', home_runs_allowed: 'Home Runs Allowed', walks_allowed: 'Walks Allowed', intentional_walks_allowed: 'Intentional Walks Allowed', hit_by_pitch_allowed: 'Hit by Pitch Allowed', runs_allowed: 'Runs Allowed', earned_runs_allowed: 'Earned Runs Allowed', era: 'ERA', whip: 'WHIP', wild_pitches: 'Wild Pitches', balks: 'Balks', club_30_30: '30–30 Club', club_40_40: '40–40 Club', club_50_50: '50–50 Club', club_20_20_20: '20–20–20 Club', club_20_20_20_20: '20–20–20–20 Club', triple_three: 'Triple Three',
  },
  es: {
    game: 'Juegos', plate_appearances: 'Apariciones al bate', at_bats: 'Turnos al bate', runs: 'Carreras', hits: 'Hits', doubles: 'Dobles', triples: 'Triples', home_runs: 'Jonrones', total_bases: 'Bases totales', rbi: 'Carreras impulsadas', steals: 'Robos', caught_stealing: 'Atrapado robando', sac_bunts: 'Toques de sacrificio', sac_flies: 'Elevados de sacrificio', walks: 'Bases por bolas', intentional_walks: 'Bases por bolas intencionales', hit_by_pitch: 'Golpeado por lanzamiento', strikeouts: 'Ponches', grounded_into_double_play: 'Doble play', batting_average: 'Promedio de bateo', on_base_percentage: 'Porcentaje de embasado', slugging_percentage: 'Porcentaje de slugging', ops: 'OPS', appearances: 'Apariciones', starts: 'Aperturas', complete_games: 'Juegos completos', shutouts: 'Blanqueadas', no_walks: 'Juegos sin bases por bolas', wins: 'Victorias', losses: 'Derrotas', saves: 'Salvamentos', holds: 'Holds', win_rate: 'Porcentaje de victorias', batters_faced: 'Bateadores enfrentados', innings_pitched: 'Entradas lanzadas', hits_allowed: 'Hits permitidos', home_runs_allowed: 'Jonrones permitidos', walks_allowed: 'Bases por bolas permitidas', intentional_walks_allowed: 'Bases por bolas intencionales permitidas', hit_by_pitch_allowed: 'Golpeados por lanzamiento', runs_allowed: 'Carreras permitidas', earned_runs_allowed: 'Carreras limpias permitidas', era: 'ERA', whip: 'WHIP', wild_pitches: 'Lanzamientos descontrolados', balks: 'Balks', club_30_30: 'Club 30–30', club_40_40: 'Club 40–40', club_50_50: 'Club 50–50', club_20_20_20: 'Club 20–20–20', club_20_20_20_20: 'Club 20–20–20–20', triple_three: 'Triple Triple',
  },
};

type MetaKey = `${'npb' | 'mlb'}_${'career' | 'season' | 'leader'}`;
const PAGE_META: Record<MetaKey, { title: Record<Locale, string> }> = {
  npb_career: {
    title: { ja: 'NPB通算記録', en: 'Nippon Professional Baseball (NPB) Career Records', es: 'Récords de carrera de Béisbol Profesional Japonés (NPB)' },
  },
  npb_season: {
    title: { ja: 'NPB年度別', en: 'Nippon Professional Baseball (NPB) Year-by-Year Records', es: 'Récords año por año de Béisbol Profesional Japonés (NPB)' },
  },
  mlb_career: {
    title: { ja: 'MLB通算記録', en: 'Major League Baseball (MLB) Career Records', es: 'Récords de carrera de Grandes Ligas (MLB)' },
  },
  mlb_season: {
    title: { ja: 'MLBシーズン記録', en: 'Major League Baseball (MLB) Season Records', es: 'Récords de temporada de Grandes Ligas (MLB)' },
  },
  npb_leader: {
    title: { ja: 'NPB年度別リーダー', en: 'Nippon Professional Baseball (NPB) Year-by-Year Leaders', es: 'Líderes por año de Béisbol Profesional Japonés (NPB)' },
  },
  mlb_leader: {
    title: { ja: 'MLB年度別リーダー', en: 'Major League Baseball (MLB) Year-by-Year Leaders', es: 'Líderes por año de Grandes Ligas (MLB)' },
  },
};

const HOME_LABEL: Record<Locale, string> = { ja: 'ホーム', en: 'Home', es: 'Inicio' };
const PLAYER_TYPE_TAB_LABELS: Record<PlayerType, { ja: string; en: string; es: string }> = {
  batter: { ja: '野手', en: 'Batters', es: 'Bateadores' },
  pitcher: { ja: '投手', en: 'Pitchers', es: 'Lanzadores' },
};

function getPlayerType(traitId: string): 'batter' | 'pitcher' | null {
  if (traitId.includes('_batter_')) return 'batter';
  if (traitId.includes('_pitcher_')) return 'pitcher';
  return null;
}

function extractMetricSuffix(traitId: string, scope: Props['scope']): string | null {
  if (scope === 'leader') {
    const m = traitId.match(/_season_leader_(.+)$/);
    return m ? m[1] : null;
  }
  if (scope === 'season') {
    const m = traitId.match(/_season_(.+)$/);
    return m ? m[1] : null;
  }
  if (scope === 'career') {
    const m = traitId.match(/_career_(.+)$/);
    return m ? m[1] : null;
  }
  return null;
}

function normalizeMetricSuffix(suffix: string): string {
  if (suffix.startsWith('era_')) return 'era';
  if (suffix.startsWith('win_rate_')) return 'win_rate';
  return suffix;
}

export default function PlayerTraitListClient({
  locale,
  traits,
  league,
  scope,
  careerListMode = 'ranking',
}: Props) {
  const t = (ja: string, en: string, es: string) =>
    locale === 'en' ? en : locale === 'es' ? es : ja;

  const traitName = (tr: TraitMeta) => {
    if (locale === 'en') return tr.name_en ?? tr.trait_id;
    if (locale === 'es') return tr.name_es ?? tr.name_en ?? tr.trait_id;
    return tr.name_ja ?? tr.trait_id;
  };

  const traitLinkName = (tr: TraitMeta) => {
    const name = traitName(tr);
    if (scope === 'career') return name.replace(locale === 'ja' ? /^通算/ : locale === 'es' ? /^Carrera\s*/i : /^Career\s*/i, '');
    if (scope === 'leader') return name.replace(locale === 'ja' ? /^最多/ : locale === 'es' ? /^(?:Más|Mayor(?:es)?|Máximo(?:s)?)\s*/i : /^Most\s*/i, '');
    if (scope === 'season') {
      const suffix = extractMetricSuffix(tr.trait_id, 'season');
      const metric = suffix ? normalizeMetricSuffix(suffix) : null;
      const fallback = locale === 'ja' ? name.replace(/^シーズン/, '') : locale === 'es' ? name.replace(/^Temporada\s*/i, '') : name.replace(/^Season\s*/i, '');
      return metric ? SEASON_LINK_LABELS[locale][metric] ?? fallback : fallback;
    }
    return name;
  };

  const buildThresholdValues = (minValue: number, maxValue: number, stepValue: number): number[] => {
    const values: number[] = [];
    let current = minValue;
    while (current <= maxValue + 1e-9) {
      values.push(Math.round(current * 1e9) / 1e9);
      current += stepValue;
    }
    return values;
  };

  const toThresholdParam = (threshold: number): string => {
    if (Number.isInteger(threshold)) return String(Math.trunc(threshold));
    return String(Math.round(threshold * 1e9) / 1e9);
  };

  const buildTraitHref = (tr: TraitMeta, threshold?: number) => {
    const rawBasePath = getTraitPathFromMeta(tr) || '/';
    const basePath = isCareerProjectionMode
      ? rawBasePath.replace(/^\/(npb|mlb)-career\//, '/$1-career-projection/')
      : rawBasePath;
    const localizedPath = withLocalePrefix(locale, basePath);
    if (threshold === undefined) return localizedPath;
    const thresholdOverrides = getTraitThresholdSlugValues(tr.trait_id);
    if (thresholdOverrides.length > 1) {
      const token = formatTraitThresholdSlugToken(tr.trait_id, threshold);
      return withLocalePrefix(locale, `${basePath}-${token}`);
    }
    return {
      pathname: localizedPath,
      query: { threshold: toThresholdParam(threshold) },
    };
  };

  const isCareerScope = scope === 'career';
  const isCareerProjectionMode = isCareerScope && careerListMode === 'projection';
  const careerRankingPath = league === 'npb' ? '/npb-career' : '/mlb-career';
  const careerProjectionPath = league === 'npb' ? '/npb-career-projection' : '/mlb-career-projection';
  const leagueHomePath = league === 'npb' ? '/npb' : '/mlb';
  const seasonPath = league === 'npb' ? '/npb-season' : '/mlb-season';
  const leaderPath = league === 'npb' ? '/npb-leader' : '/mlb-leader';
  const alternateCareerPath = isCareerProjectionMode ? careerRankingPath : careerProjectionPath;
  const emptyStateLinks = isCareerScope
    ? [
        {
          href: withLocalePrefix(locale, alternateCareerPath),
          label: isCareerProjectionMode
            ? t('通算記録ランキングを見る', 'View career rankings', 'Ver ranking de carrera')
            : t('達成予測モードを見る', 'View projection mode', 'Ver modo de proyección'),
          note: isCareerProjectionMode
            ? t('通常の通算記録へ。', 'Open the regular career records.', 'Abre los registros de carrera normales.')
            : t('達成予測モードへ。', 'Open projection mode.', 'Abre el modo de proyección.'),
        },
        {
          href: withLocalePrefix(locale, seasonPath),
          label: t('シーズン記録を見る', 'View season records', 'Ver registros de temporada'),
          note: t('シーズン記録へ。', 'Open season records.', 'Abre los registros de temporada.'),
        },
        {
          href: withLocalePrefix(locale, leaderPath),
          label: t('タイトル・ランキングを見る', 'View leaderboards', 'Ver rankings'),
          note: t('ランキング一覧へ。', 'Open leaderboard pages.', 'Abre las páginas de rankings.'),
        },
      ]
    : [
        {
          href: withLocalePrefix(locale, careerRankingPath),
          label: t('通算記録を見る', 'View career records', 'Ver registros de carrera'),
          note: t('通算記録へ。', 'Open career records.', 'Abre los registros de carrera.'),
        },
        {
          href: withLocalePrefix(locale, leaderPath),
          label: t('タイトル・ランキングを見る', 'View leaderboards', 'Ver rankings'),
          note: t('ランキング一覧へ。', 'Open leaderboard pages.', 'Abre las páginas de rankings.'),
        },
        {
          href: withLocalePrefix(locale, leagueHomePath),
          label: t('リーグトップへ', 'Go to league home', 'Ir a la página principal de la liga'),
          note: t('リーグトップへ。', 'Open the league home page.', 'Abre la página principal de la liga.'),
        },
      ];

  const meta = PAGE_META[`${league}_${scope}`];
  const pageTitle = isCareerProjectionMode
    ? t(
      league === 'npb' ? 'プロ野球（NPB）通算記録達成まで' : 'メジャーリーグ（MLB）通算記録達成まで',
      league === 'npb' ? 'Nippon Professional Baseball (NPB) Career Milestone This Season' : 'Major League Baseball (MLB) Career Milestone This Season',
      league === 'npb' ? 'Meta de Carrera de Béisbol Profesional Japonés (NPB) en Esta Temporada' : 'Meta de Carrera de Grandes Ligas (MLB) en Esta Temporada',
    )
    : meta.title[locale];
  const isCareerProjectionEligibleTrait = (tr: TraitMeta): boolean => {
    if (scope !== 'career' || !isCareerProjectionMode) return true;
    if (tr.record_scope !== 'career') return false;
    const suffixRaw = extractMetricSuffix(tr.trait_id, 'career');
    if (!suffixRaw) return false;
    const suffix = normalizeMetricSuffix(suffixRaw);
    if (CAREER_PROJECTION_EXCLUDED_SUFFIXES.has(suffix)) return false;
    if (tr.threshold_type !== 'gte') return false;
    const hasStepRange =
      tr.min_value !== null
      && tr.min_value !== undefined
      && tr.max_value !== null
      && tr.max_value !== undefined
      && tr.step_value !== null
      && tr.step_value !== undefined
      && tr.step_value > 0;
    return hasStepRange;
  };

  const leagueTraits = traits.filter((tr) => {
    if (tr.league_scope !== league) return false;
    if (scope === 'leader') return tr.record_scope === 'season' && isLeaderTraitId(tr.trait_id);
    if (!(tr.record_scope === scope && !isLeaderTraitId(tr.trait_id))) return false;
    return isCareerProjectionEligibleTrait(tr);
  });
  // 日米通算はプロ野球（NPB）の通算ランキングにのみ表示
  const combinedTraits = league === 'npb' && scope === 'career'
    ? traits.filter((tr) => tr.league_scope === 'mlb_npb' && isCareerProjectionEligibleTrait(tr))
    : [];

  const originalIndexMap = new Map<string, number>();
  traits.forEach((tr, idx) => originalIndexMap.set(tr.trait_id, idx));

  const sortTraitsForList = (
    list: TraitMeta[],
    playerType: 'batter' | 'pitcher',
  ): TraitMeta[] => {
    const orderMap = playerType === 'batter' ? BATTER_ORDER_MAP : PITCHER_ORDER_MAP;
    return [...list].sort((a, b) => {
      const suffixA = extractMetricSuffix(a.trait_id, scope);
      const suffixB = extractMetricSuffix(b.trait_id, scope);
      const normA = suffixA ? normalizeMetricSuffix(suffixA) : null;
      const normB = suffixB ? normalizeMetricSuffix(suffixB) : null;

      const orderA = normA ? orderMap.get(normA) : undefined;
      const orderB = normB ? orderMap.get(normB) : undefined;

      const specialA = scope !== 'career' && playerType === 'batter' && normA ? BATTER_SPECIAL_SEASON_ORDER.get(normA) : undefined;
      const specialB = scope !== 'career' && playerType === 'batter' && normB ? BATTER_SPECIAL_SEASON_ORDER.get(normB) : undefined;

      const rankA = orderA ?? specialA ?? 10_000;
      const rankB = orderB ?? specialB ?? 10_000;
      if (rankA !== rankB) return rankA - rankB;

      const idxA = originalIndexMap.get(a.trait_id) ?? 10_000_000;
      const idxB = originalIndexMap.get(b.trait_id) ?? 10_000_000;
      return idxA - idxB;
    });
  };

  const sortCombinedTraits = (list: TraitMeta[]): TraitMeta[] => {
    const batters = sortTraitsForList(
      list.filter((tr) => getPlayerType(tr.trait_id) === 'batter'),
      'batter',
    );
    const pitchers = sortTraitsForList(
      list.filter((tr) => getPlayerType(tr.trait_id) === 'pitcher'),
      'pitcher',
    );
    return [...batters, ...pitchers];
  };

  const groups: Group[] = GROUPS
    .filter((d) => d.recordScope === scope)
    .map(({ key, labelJa, labelEn, labelEs, playerType, recordScope }) => ({
      key,
      labelJa,
      labelEn,
      labelEs,
      traits: sortTraitsForList(
        leagueTraits.filter(
          (tr) => {
          if (getPlayerType(tr.trait_id) !== playerType) return false;
          if (recordScope === 'leader') return isLeaderTraitId(tr.trait_id);
          return tr.record_scope === recordScope && !isLeaderTraitId(tr.trait_id);
          },
        ),
        playerType,
      ),
    })).filter((g) => g.traits.length > 0);

  const groupsByType = useMemo<Record<PlayerType, Group[]>>(
    () => ({
      batter: groups.filter((g) => g.key.startsWith('batter_')),
      pitcher: groups.filter((g) => g.key.startsWith('pitcher_')),
    }),
    [groups],
  );

  const sortedCombinedTraits = useMemo(
    () => sortCombinedTraits(combinedTraits),
    [combinedTraits],
  );

  const combinedTraitsByType = useMemo<Record<PlayerType, TraitMeta[]>>(
    () => ({
      batter: sortedCombinedTraits.filter((tr) => getPlayerType(tr.trait_id) === 'batter'),
      pitcher: sortedCombinedTraits.filter((tr) => getPlayerType(tr.trait_id) === 'pitcher'),
    }),
    [sortedCombinedTraits],
  );

  const availablePlayerTabs = useMemo<PlayerType[]>(
    () =>
      (['pitcher', 'batter'] as const).filter(
        (type) => groupsByType[type].length > 0 || combinedTraitsByType[type].length > 0,
      ),
    [groupsByType, combinedTraitsByType],
  );

  const renderTraitLinks = (tr: TraitMeta) => {
    const overrideThresholds = getTraitThresholdSlugValues(tr.trait_id);
    const stepThresholds = (
      tr.min_value !== null
      && tr.min_value !== undefined
      && tr.max_value !== null
      && tr.max_value !== undefined
      && tr.step_value !== null
      && tr.step_value !== undefined
      && tr.step_value > 0
    )
      ? buildThresholdValues(tr.min_value, tr.max_value, tr.step_value)
      : [];
    const thresholds = overrideThresholds.length > 0 ? overrideThresholds : stepThresholds;
    const isSeasonStepTrait = scope === 'season' && thresholds.length > 1;

    if (!isSeasonStepTrait) {
      return [(
        <li key={tr.trait_id} className="awards-list-item">
          <div className="trait-list-link-row">
            <Link href={buildTraitHref(tr)}>
              {traitLinkName(tr)}
            </Link>
          </div>
        </li>
      )];
    }

    return thresholds.map((threshold) => {
      const thresholdName = traitLinkName(tr);
      return (
        <li key={`${tr.trait_id}:${toThresholdParam(threshold)}`} className="awards-list-item">
          <div className="trait-list-link-row">
            <Link href={buildTraitHref(tr, threshold)}>
              {thresholdName}
            </Link>
          </div>
        </li>
      );
    });
  };

  return (
    <div className="info-page">
      <nav className="awards-breadcrumb">
        <Link href={withLocalePrefix(locale, '/')}>{HOME_LABEL[locale]}</Link>
        {' › '}
        <span>{pageTitle}</span>
      </nav>
      <h1>{pageTitle}</h1>

      {isCareerScope && (
        <div
          className="info-tabs"
          role="tablist"
          aria-label={t('通算記録表示切替', 'Switch career views', 'Cambiar vistas de carrera')}
        >
          <Link
            href={withLocalePrefix(locale, careerRankingPath)}
            role="tab"
            aria-selected={!isCareerProjectionMode}
            className={`info-tab-button ${!isCareerProjectionMode ? 'active' : ''}`}
          >
            {t(CAREER_MODE_TABS.ranking.ja, CAREER_MODE_TABS.ranking.en, CAREER_MODE_TABS.ranking.es)}
          </Link>
          <Link
            href={withLocalePrefix(locale, careerProjectionPath)}
            role="tab"
            aria-selected={isCareerProjectionMode}
            className={`info-tab-button ${isCareerProjectionMode ? 'active' : ''}`}
          >
            {t(CAREER_MODE_TABS.projection.ja, CAREER_MODE_TABS.projection.en, CAREER_MODE_TABS.projection.es)}
          </Link>
        </div>
      )}

      {availablePlayerTabs.length === 0 ? (
        <PageEmptyState
          id="player-trait-list-empty-state"
          title={t('対象の指標がありません。', 'No traits are available for this view.', 'No hay criterios disponibles para esta vista.')}
          paragraphs={[
            t(
              'このリーグ・表示条件では指標が見つかりませんでした。',
              'No traits were found for this league and view.',
              'No se encontraron criterios para esta liga y vista.',
            ),
            t(
              '通算記録・シーズン記録・タイトル一覧を切り替えて探してください。',
              'Try career records, season records, or leaderboard pages.',
              'Prueba registros de carrera, de temporada o de rankings.',
            ),
          ]}
          links={emptyStateLinks}
          linksLabel={t('関連リンク', 'Related links', 'Enlaces relacionados')}
        />
      ) : (
        <>
          <div className="trait-list-columns">
            {(['pitcher', 'batter'] as const).map((playerType) => {
              const playerGroups = groupsByType[playerType] ?? [];
              if (playerGroups.length === 0) return null;
              return (
                <section key={playerType} className="trait-list-column">
                  <h2 className="awards-category-title">
                    {t(
                      PLAYER_TYPE_TAB_LABELS[playerType].ja,
                      PLAYER_TYPE_TAB_LABELS[playerType].en,
                      PLAYER_TYPE_TAB_LABELS[playerType].es,
                    )}
                  </h2>
                  {playerGroups.map((g) => (
                    <ul key={g.key} className="awards-list">
                      {g.traits.reduce<ReactElement[]>((acc, trait) => {
                        acc.push(...renderTraitLinks(trait));
                        return acc;
                      }, [])}
                    </ul>
                  ))}
                </section>
              );
            })}
          </div>

          {sortedCombinedTraits.length > 0 && (
            <div className="awards-category-section">
              <h2 className="awards-category-title">
                {t(COMBINED_LABEL.ja, COMBINED_LABEL.en, COMBINED_LABEL.es)}
              </h2>
              <ul className="awards-list">
                {sortedCombinedTraits.reduce<ReactElement[]>((acc, trait) => {
                  acc.push(...renderTraitLinks(trait));
                  return acc;
                }, [])}
              </ul>
            </div>
          )}
        </>
      )}

      <ScrollToTopButton />
    </div>
  );
}
